/** @shell standard @header bar */
import React, { useMemo, useCallback, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated from 'react-native-reanimated';
import { ScreenHeader, useHeaderTopPadding, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { appTheme, useThemeColors } from '../shared/theme';
import { useTranslation } from '../shared/i18n';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { StackScreenShell, STACK_SCROLL_GUTTER } from '../shared/theme/studio/stackScreenShell';
import { journeyStepEntering } from '../shared/theme/studio/journeyMotion';
import { JOURNEY_STEP_COUNT, useJourneyWizard } from '../data/domains/journey';
import { JourneyProgressBar } from './components/JourneyProgressBar';
import { JourneyGoalStep } from './components/JourneyGoalStep';
import { JourneyAboutStep } from './components/JourneyAboutStep';
import { JourneyActivityStep } from './components/JourneyActivityStep';
import { JourneyWizardFooter } from './components/JourneyWizardFooter';
import { JOURNEY_PROGRESS_HEADER_BLOCK_HEIGHT, JOURNEY_SCROLL_FOOTER_GAP } from './components/journeyLayout';

export const JourneyScreen = () => {
  const { t } = useTranslation();
  const leave_journey = useExitOrGoBack();
  const top_pad = useHeaderTopPadding(false, JOURNEY_PROGRESS_HEADER_BLOCK_HEIGHT);
  const { scrollY, onScroll } = useScreenHeaderScroll();
  const colors = useThemeColors() ?? appTheme.colors;
  const [scroll_enabled, set_scroll_enabled] = useState(true);

  const wizard = useJourneyWizard();

  const born_in_year_text = useMemo(
    () => t('journey.bornInYear').replace('{{year}}', String(wizard.birth.getFullYear())),
    [t, wizard.birth],
  );

  const step_caption = useMemo(() => {
    if (wizard.step === 0) {
      return t('journey.whatsYourGoal');
    }
    if (wizard.step === 1) {
      return t('journey.aboutYou');
    }
    return t('journey.activityLevel');
  }, [wizard.step, t]);

  const on_header_back = useCallback(() => {
    if (wizard.step === 0) {
      leave_journey();
      return;
    }
    wizard.on_back_step();
  }, [wizard.step, wizard.on_back_step, leave_journey]);

  const on_wheel_gesture_active = useCallback((active: boolean) => {
    set_scroll_enabled(!active);
  }, []);

  const header_progress = (
    <View style={styles.header_progress_wrap}>
      <JourneyProgressBar
        colors={colors}
        step={wizard.step}
        total_steps={JOURNEY_STEP_COUNT}
        step_label={step_caption}
      />
    </View>
  );

  return (
    <StackScreenShell colors={colors}>
      <ScreenHeader
        title={t('journey.yourJourney')}
        onBack={on_header_back}
        scrollY={scrollY}
        bottomContent={header_progress}
      />

      <Animated.ScrollView
        style={styles.scroll}
        onScroll={onScroll}
        scrollEventThrottle={16}
        scrollEnabled={scroll_enabled}
        contentContainerStyle={[
          styles.scroll_content,
          {
            paddingTop: top_pad + appTheme.spacing.sm,
            paddingBottom: JOURNEY_SCROLL_FOOTER_GAP,
          },
        ]}
        contentInsetAdjustmentBehavior="never"
        automaticallyAdjustContentInsets={false}
        nestedScrollEnabled
        removeClippedSubviews={false}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        {wizard.step === 0 ? (
          <Animated.View key={0} entering={journeyStepEntering()} style={styles.step_shell}>
            <JourneyGoalStep colors={colors} goal={wizard.goal} on_select_goal={wizard.select_goal} />
          </Animated.View>
        ) : null}

        {wizard.step === 1 ? (
          <Animated.View key={1} entering={journeyStepEntering()} style={styles.step_shell}>
            <JourneyAboutStep
              colors={colors}
              age={wizard.age}
              gender={wizard.gender}
              weight={wizard.weight}
              height={wizard.height}
              born_in_year_text={born_in_year_text}
              on_age_change={wizard.set_age_years}
              on_gender_change={wizard.setGender}
              on_weight_change={wizard.set_weight_kg}
              on_height_change={wizard.set_height_cm}
              on_wheel_gesture_active_change={on_wheel_gesture_active}
            />
          </Animated.View>
        ) : null}

        {wizard.step === 2 ? (
          <Animated.View key={2} entering={journeyStepEntering()} style={styles.step_shell}>
            <JourneyActivityStep
              colors={colors}
              activity={wizard.activity}
              on_select_activity={wizard.select_activity}
            />
          </Animated.View>
        ) : null}
      </Animated.ScrollView>

      <JourneyWizardFooter
        colors={colors}
        continue_label={wizard.continue_label}
        continue_disabled={!wizard.can_continue || wizard.loading}
        show_back={wizard.step > 0}
        footer_hidden={wizard.step === 0 && !wizard.goal}
        back_label={t('common.back')}
        on_back={wizard.on_back_step}
        on_continue={wizard.on_continue}
      />
    </StackScreenShell>
  );
};

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  scroll_content: {
    paddingHorizontal: STACK_SCROLL_GUTTER,
    flexGrow: 1,
  },
  header_progress_wrap: {
    paddingHorizontal: STACK_SCROLL_GUTTER,
  },
  step_shell: {
    flexGrow: 1,
  },
});

export default JourneyScreen;
